import {
  TableRow,
  TableCell,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { fmtDateParts, initials } from "./LeadUtils";
import {
  getColorFromPalette,
  getSourceStyles,
  getStatusStyles,
  STATUS_OPTIONS,
} from "../../../config/leadStatus";
import { TelecallerDropdown } from "./TelecallerDropdown";
import { StatusDropdown } from "./StatusDropdown";

// Component to display a single follow-up call in the table
const FollowUpCell = ({ callLog }) => {
  if (!callLog) {
    return (
      <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>-</Typography>
    );
  }

  const callTime = callLog.call_time ? new Date(callLog.call_time) : null;
  const isRNR = callLog.rnr === true;
  const notes = callLog.notes || "";

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    return `${day} ${month}`;
  };
  const formatTime = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }).toLowerCase();
  };

  // Truncate notes for display
  const truncateNotes = (text, maxLength = 30) => {
    if (!text) return "-";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <Stack spacing={0.25}>
      {/* Date */}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: isRNR ? "#DC2626" : "#475569"
        }}
      >
        {formatDate(callTime)}
      </Typography>
      {/* Time */}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 500,
          color: isRNR ? "#DC2626" : "#94A3B8"
        }}
      >
        {formatTime(callTime)}
      </Typography>
      {/* Notes */}
      <Tooltip title={notes || "No notes"} arrow placement="top">
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 500,
            color: isRNR ? "#DC2626" : "#64748B",
            maxWidth: 80,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: notes ? "pointer" : "default"
          }}
        >
          {truncateNotes(notes)}
        </Typography>
      </Tooltip>
      {/* RNR Status Badge */}
      {isRNR && (
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            color: "#DC2626",
            bgcolor: "#FEE2E2",
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            display: "inline-block",
            width: "fit-content"
          }}
        >
          RNR
        </Typography>
      )}
    </Stack>
  );
};

const LeadRow = ({
  l,
  onViewDetails,
  telecallers = [],
  treatments = [],
  statusOptions,
  onStatusChange,
  onTelecallerChange,
  onTreatmentChange,
  onContactInfoClick,
}) => {
  const theme = useTheme();
  const d = fmtDateParts(l.createdAt);
  const statusKey = l.current_status?.key || l.responseLabel || "new";
  const sourceStyle = getSourceStyles(l.source);
  const avatarStyle = getColorFromPalette(l.name);

  const TREATMENT_OPTIONS = (treatments || []).map((t) => ({
    key: t,
    label: t,
  }));

  const norm = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const getTreatmentStyles = (t) => {
    const s = getColorFromPalette(String(t || "—"));
    return {
      bgcolor: s.bgcolor,
      color: s.color,
      label: String(t || "—"),
    };
  };

  const firstLetter = (label = "") => {
    const t = String(label).trim();
    return (t[0] || "?").toUpperCase();
  };

  const TreatmentAvatars = ({ services, options, onRemove }) => {
    const serviceSet = new Set(
      (Array.isArray(services) ? services : []).map(norm),
    );

    const selected = options.filter((opt) => serviceSet.has(norm(opt.key)));

    if (!selected.length) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.6, fontStyle: "italic" }}>
          No service selected
        </Typography>
      );
    }

    const visible = selected.slice(0, 2);
    const extra = selected.length - visible.length;

    return (
      <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
        {visible.map((opt, idx) => (
          <Tooltip key={opt.key} title={opt.label} arrow>
            <Avatar
              onClick={() => onRemove?.(opt.key)}
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                fontWeight: 800,
                cursor: onRemove ? "pointer" : "default",
                bgcolor: getTreatmentStyles(opt.label).color,

                ml: idx === 0 ? 0 : -1.1,
                border: "2px solid #fff",
                boxSizing: "content-box",
              }}
            >
              {firstLetter(opt.label)}
            </Avatar>
          </Tooltip>
        ))}

        {extra > 0 && (
          <Tooltip
            title={selected
              .slice(2)
              .map((x) => x.label)
              .join(", ")}
            arrow
          >
            <Chip
              label={`+${extra}`}
              size="small"
              sx={{
                height: 28,
                ml: -1.1,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 11,
                px: 0.5,
                bgcolor: "#EEF2FF",
                color: "#4F46E5",
                border: "2px solid #fff",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <TableRow
      hover
      onClick={() => onViewDetails(l)}
      sx={{ "& td": { borderBottom: "1px solid #F1F5F9", py: 1, px: 0.75, overflow: "hidden" }, cursor: "pointer" }}
    >
      {/* DATE */}
      <TableCell>
        <Stack spacing={0}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1E293B" }}>
            {d?.top || "-"}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1E293B" }}>
            {d?.mid || ""}
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 500, color: "#94A3B8" }}>
            {d?.bot || ""}
          </Typography>
        </Stack>
      </TableCell>

      {/* SOURCE */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: sourceStyle.bgcolor,
              color: sourceStyle.color,
            }}
          >
            {sourceStyle.icon}
          </Avatar>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#475569",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            textTransform={"capitalize"}
          >
            {l.source || "-"}
          </Typography>
        </Stack>
      </TableCell>

      {/* NAME */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: avatarStyle.bgcolor,
              color: avatarStyle.color,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {initials(l.name)}
          </Avatar>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {l.name}
          </Typography>
        </Stack>
      </TableCell>

      {/* CONTACT - Click to open popup */}
      <TableCell>
        <Stack
          direction="row"
          spacing={0.5}
          onClick={(e) => { e.stopPropagation(); onContactInfoClick?.({
            phone: l.phone,
            email: l.email,
            address: l.location || l._raw?.address || "",
            clientName: l.name,
          }); }}
          sx={{ cursor: "pointer" }}
        >
          <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#E2E8F0" },
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: "#64748B" }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#E2E8F0" },
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 14, color: "#64748B" }} />
          </IconButton>
        </Stack>
      </TableCell>

      {/* TREATMENT TYPE */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <TreatmentAvatars
          services={l.services}
          options={TREATMENT_OPTIONS}
          onRemove={(key) => {
            const curr = Array.isArray(l.services) ? l.services : [];
            const next = curr.filter((x) => x !== key);
            onTreatmentChange?.(l._id, next);
          }}
        />
      </TableCell>

      {/* TELECALLER */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <TelecallerDropdown
          value={l.telecallerId || ""}
          telecallers={telecallers}
          onSelect={(userId) => onTelecallerChange(l._id, userId)}
        />
      </TableCell>

      {/* FOLLOW-UP COLUMNS - Dynamic based on available records */}
      {/*
        Logic: followUps array is sorted most recent first [0] to oldest last.
        We display from oldest to newest (left to right).
        Column 1 = oldest available, Column 4 = most recent available.
        If fewer than 4 follow-ups exist, empty columns show "-" at the end.
      */}
      {(() => {
        const followUps = l.followUps || [];
        const count = followUps.length;

        // Helper: Get follow-up for column position (1-4)
        // Column 1 shows oldest, Column 4 shows newest
        // If count < column position, return undefined (shows "-")
        const getFollowUpForColumn = (columnPos) => {
          if (columnPos > count) return undefined;
          // followUps[0] = newest, followUps[count-1] = oldest
          // Column 1 should show oldest = followUps[count - 1]
          // Column 2 should show second oldest = followUps[count - 2]
          // etc.
          return followUps[count - columnPos];
        };

        return (
          <>
            {/* FOLLOW-UP 1 (Oldest available) */}
            <TableCell>
              <FollowUpCell callLog={getFollowUpForColumn(1)} />
            </TableCell>

            {/* FOLLOW-UP 2 */}
            <TableCell>
              <FollowUpCell callLog={getFollowUpForColumn(2)} />
            </TableCell>

            {/* FOLLOW-UP 3 */}
            <TableCell>
              <FollowUpCell callLog={getFollowUpForColumn(3)} />
            </TableCell>

            {/* FOLLOW-UP 4 (Most Recent available) */}
            <TableCell>
              <FollowUpCell callLog={getFollowUpForColumn(4)} />
            </TableCell>
          </>
        );
      })()}

      {/* STATUS */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <StatusDropdown
          value={statusKey}
          options={statusOptions || STATUS_OPTIONS}
          getStyles={(value) => {
            if (statusOptions) {
              const item = statusOptions.find((o) => o.key === value);
              return item
                ? {
                    label: item.label,
                    color: item.color,
                    bgcolor: item.bgcolor,
                    icon: item.icon,
                  }
                : getStatusStyles(value);
            }
            return getStatusStyles(value);
          }}
          onSelect={(status) => onStatusChange?.(l._id, status)}
        />
      </TableCell>

      {/* ACTION */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Button
          onClick={() => onViewDetails(l)}
          variant="outlined"
          size="small"
          sx={{
            minWidth: "auto",
            height: 28,
            px: 1.5,
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 11,
            borderColor: "#E2E8F0",
            color: theme.palette.primary.main,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderColor: alpha(theme.palette.primary.main, 0.3),
            },
          }}
        >
          View Details
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default LeadRow;
